/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { errorMasterService } from '../../services/error-master.service';
import { errorAlertBindingService, type ErrorAlertBinding } from '../../services/error-alert-binding.service';
import { notificationMastersService } from '../../services/notification-masters.service';
import type { ErrorMaster } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { Field, Section, Pill, type PillTone } from '../../components/common/detail-view';

const ErrorMasterView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'errorMaster',
    basePath: '/error-master',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.ERROR_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.ERROR_MASTER);

  const [errorMaster, setErrorMaster] = useState<ErrorMaster | null>(null);
  const [binding, setBinding] = useState<ErrorAlertBinding | null>(null);
  const [notificationLabel, setNotificationLabel] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Set breadcrumb title to show error code instead of UUID
  useBreadcrumbTitle(errorMaster?.errorCode);

  // Stable fetch function
  const fetchErrorMaster = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await errorMasterService.getById(id);
      setErrorMaster(data);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to fetch error master details'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Run once when ID changes
  useEffect(() => {
    if (!isReady || !id) return;
    fetchErrorMaster();
  }, [isReady, id, fetchErrorMaster]);

  // Load the alert binding (if any) for this error, plus a friendly label for
  // the notification it points at. Best-effort — a missing binding is normal.
  useEffect(() => {
    const errorCode = errorMaster?.errorCode;
    if (!errorCode) return;
    let cancelled = false;
    (async () => {
      try {
        const b = await errorAlertBindingService.getForError(errorCode);
        if (cancelled) return;
        setBinding(b);
        if (b?.notificationMasterId) {
          try {
            const n = await notificationMastersService.getById(b.notificationMasterId);
            if (!cancelled) setNotificationLabel(n.description || b.notificationMasterId);
          } catch {
            if (!cancelled) setNotificationLabel(b.notificationMasterId);
          }
        }
      } catch {
        if (!cancelled) setBinding(null);
      }
    })();
    return () => { cancelled = true; };
  }, [errorMaster?.errorCode]);

  // Human-readable summary of the binding's trigger rules.
  const alertModeSummary = (b: ErrorAlertBinding): string => {
    const r = b.rules || ({} as any);
    if (r.triggerMode === 'THRESHOLD') {
      return `After ${r.threshold?.count ?? 10} occurrences within ${r.threshold?.windowMinutes ?? 5} min`;
    }
    if (r.triggerMode === 'DIGEST') {
      return `Digest every ${r.digest?.every ?? 1} ${(r.digest?.unit ?? 'HOURS').toLowerCase()}`;
    }
    return 'Immediate — on the first occurrence';
  };

  // Get severity color
  const severityTone = (severity: string): PillTone => {
    switch (severity) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'amber';
      case 'MEDIUM': return 'blue';
      case 'LOW': return 'green';
      default: return 'neutral';
    }
  };

  // Source type icon
  const getSourceTypeIcon = (type: string): string => {
    switch (type?.toUpperCase()) {
      case 'API': return 'pi pi-server';
      case 'SCREEN': return 'pi pi-desktop';
      case 'FUNCTION': return 'pi pi-code';
      default: return 'pi pi-box';
    }
  };

  const handleEdit = () => { if (id) navigateToEdit(id); };
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await errorMasterService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete error master'));
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !errorMaster) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Error master not found'} className="mb-4 w-full" />
        <Button label="Back to Error Masters" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={handleDeleteCancel} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  return (
    <div className="p-3" data-testid="SCR-ErrorMaster-View">
      {/* Header Section */}
      <ScreenHeader
        icon="pi pi-exclamation-triangle"
        title="Error Master Details"
        onBack={() => navigateToList()}
      />

      {/* Error Master Profile Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          <div className="w-12 h-12 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-exclamation-circle text-red-600 dark:text-red-400" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <h2 className="text-base font-semibold text-ink">{errorMaster.errorCode}</h2>
              {(errorMaster as any).errorType && (
                <Pill tone="blue">{(errorMaster as any).errorType}</Pill>
              )}
              <Pill tone={severityTone(errorMaster.errorSeverity)}>{errorMaster.errorSeverity}</Pill>
            </div>
            <div className="flex items-center gap-2 text-xs text-ink-muted">
              {(errorMaster as any).sourceType && (
                <span className="flex items-center gap-1">
                  <i className={getSourceTypeIcon((errorMaster as any).sourceType)} style={{ fontSize: '0.75rem' }} />
                  {(errorMaster as any).sourceType}
                </span>
              )}
              {(errorMaster as any).sourceName && (
                <span>· {(errorMaster as any).sourceName}</span>
              )}
              <span>· {errorMaster.log ? 'Logging Enabled' : 'Logging Disabled'}</span>
            </div>
          </div>
        </div>

        {!((errorMaster as any).isDelete || errorMaster.isDeleted) && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && <Button label="Edit" icon="pi pi-pencil" onClick={handleEdit} size="small" />}
            {canDelete && <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={handleDeleteClick} size="small" />}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2 mt-2">
        <Section title="Source & App Information">
          <Field label="Source Type" value={(errorMaster as any).sourceType} />
          <Field label="Source Name" value={(errorMaster as any).sourceName} />
        </Section>

        <Section title="Alerting">
          {binding ? (
            <>
              <Field label="Notification" value={notificationLabel || binding.notificationMasterId} />
              <Field label="When" value={alertModeSummary(binding)} />
              <Field label="Minimum Severity" value={binding.rules?.minSeverity || 'Any'} />
              {binding.rules?.triggerMode !== 'DIGEST' && (
                <Field label="Cooldown" value={`${binding.rules?.cooldownMinutes ?? 15} min`} />
              )}
              <Field label="Status" value={binding.isActive ? 'Active' : 'Inactive'} />
            </>
          ) : (
            <Field label="Notification" value="No alerting configured for this error." />
          )}
        </Section>

        <Section title="Context Information">
          <Field label="Business Area" value={errorMaster.businessArea} />
          <Field label="Technical Area" value={errorMaster.technicalArea} />
          <Field label="Tool" value={errorMaster.tool} />
        </Section>

        <Section title="System Integration">
          <Field label="Partner System" value={errorMaster.partnerSystem} />
          <Field label="Third Party" value={errorMaster.thirdParty} />
        </Section>

        <Section title="Localized Messages" cols={1}>
          {(errorMaster.messages ?? []).length ? (errorMaster.messages ?? []).map((message, index) => (
            <div key={`${message.language}-${index}`} className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <Pill tone="blue" className="mb-1">{message.language.toUpperCase()}</Pill>
              <p className="text-sm text-ink">{message.template}</p>
            </div>
          )) : <span className="text-sm text-gray-400">-</span>}
        </Section>

        {(errorMaster.devMessage || errorMaster.helpLink || errorMaster.videoLink) && (
          <Section title="Developer Information" cols={1}>
            <Field label="Developer Message" value={errorMaster.devMessage} />
            {errorMaster.helpLink && (
              <Field label="Help Link" value={
                <a href={errorMaster.helpLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                  {errorMaster.helpLink} <i className="pi pi-external-link" style={{ fontSize: '0.75rem' }} />
                </a>
              } />
            )}
            {errorMaster.videoLink && (
              <Field label="Video Link" value={
                <a href={errorMaster.videoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                  {errorMaster.videoLink} <i className="pi pi-external-link" style={{ fontSize: '0.75rem' }} />
                </a>
              } />
            )}
          </Section>
        )}

        <AuditInfoCard
          createdAt={errorMaster.createdAt}
          updatedAt={errorMaster.updatedAt}
          createdBy={errorMaster.createdBy}
          updatedBy={(errorMaster as any).updatedBy}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog visible={deleteDialogOpen} onHide={handleDeleteCancel} header="Confirm Delete" footer={deleteDialogFooter} style={{ width: '450px' }} data-testid="ErrorMasterView.Dialog.Delete">
        <p className="text-sm text-ink-muted">Are you sure you want to delete this error master? This action cannot be undone.</p>
      </Dialog>
    </div>
  );
};

export default ErrorMasterView;
